import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { FileClock } from "lucide-react";
import type { OrderStats } from "@/api/types";
import { isLabInstance } from "@/lib/live";
import { cn } from "@/lib/utils";
import { useApi } from "../context/ApiContext";

/* Icons drawn exactly as in design/concept-a-guided.html (24px grid, 1.8 stroke). */
const ICONS = {
  backtests: <path d="M4 19V5M4 19h16M8 15l3-4 3 2 5-6" />,
  live: (
    <>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M7.5 7.5a6.5 6.5 0 0 0 0 9M16.5 7.5a6.5 6.5 0 0 1 0 9M4.6 4.6a10.5 10.5 0 0 0 0 14.8M19.4 4.6a10.5 10.5 0 0 1 0 14.8" />
    </>
  ),
  exchanges: <path d="M7 7h11l-3-3M17 17H6l3 3" />,
};

function NavItem({
  to,
  icon,
  label,
  end,
  dot,
}: {
  to: string;
  icon: keyof typeof ICONS;
  label: string;
  end?: boolean;
  dot?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className={({ isActive }) =>
        cn(
          "relative flex w-full flex-col items-center gap-1 rounded-lg py-2 text-[10px] leading-none no-underline transition-colors",
          isActive
            ? "bg-[var(--primary-soft)] text-[var(--primary)]"
            : "text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
        )
      }
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
        {ICONS[icon]}
      </svg>
      {label}
      {dot && (
        <i
          aria-hidden
          className="absolute right-3 top-1.5 h-[7px] w-[7px] rounded-full bg-[var(--success)] shadow-[0_0_0_2px_var(--bg-sidebar)]"
        />
      )}
    </NavLink>
  );
}

/** Rail from Design A: the logo (the dashboard), then Backtests / Live / Accounts. */
export default function Sidebar() {
  const api = useApi(["backtests"]);
  return (
    <aside className="flex w-16 flex-col items-center gap-1.5 border-r border-[var(--border)] bg-[var(--bg-sidebar)] px-1 py-2.5">
      <Link to="/" title="Dashboard" aria-label="TradeStorm dashboard" className="mb-2.5 flex h-[30px] w-[30px] items-center justify-center">
        <img src="/tradestorm-logo.svg" alt="" className="h-[30px] w-[30px] object-contain" />
      </Link>
      <nav aria-label="Main" className="flex w-full flex-col items-stretch gap-1.5">
        <NavItem to="/lab/backtest" icon="backtests" label="Backtests" />
        <NavItem to="/live" icon="live" label="Live" dot={api.liveRunning > 0} />
        <NavItem to="/integrations/marketplaces" icon="exchanges" label="Accounts" />
      </nav>
    </aside>
  );
}

/** The same places as a row in the top bar, on wide screens (the rail's space goes to the page). */
export function TopNav() {
  const api = useApi(["backtests", "accounts"]);
  const backtests = (api.backtests || []).filter(isLabInstance).length;
  const accounts = (api.accounts || []).filter((a) => a.enabled).length;
  const items: { to: string; icon: keyof typeof ICONS; label: string; dot?: boolean; count?: number | null; hint?: string }[] = [
    { to: "/lab/backtest", icon: "backtests", label: "Backtests", count: api.ready ? backtests : null, hint: "backtests" },
    { to: "/live", icon: "live", label: "Live", dot: api.liveRunning > 0, count: api.ready ? api.liveRunning : null, hint: "running live" },
    { to: "/integrations/marketplaces", icon: "exchanges", label: "Accounts", count: api.ready ? accounts : null, hint: "switched on" },
  ];
  return (
    <nav aria-label="Main" className="hidden shrink-0 items-center gap-0.5 md:flex">
      {items.map(({ to, icon, label, dot, count, hint }) => (
        <NavLink
          key={to}
          to={to}
          title={count != null ? `${label}: ${count.toLocaleString()} ${hint}` : label}
          aria-label={label}
          className={({ isActive }) =>
            cn(
              "relative flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] no-underline transition-colors",
              isActive
                ? "bg-[var(--primary-soft)] font-medium text-[var(--primary)]"
                : "text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
            )
          }
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            {ICONS[icon]}
          </svg>
          {/* Tablet widths keep only the icons, so the page title still fits. */}
          <span className="hidden lg:inline">{label}</span>
          {count != null && (
            <span className="num rounded-full bg-[var(--bg-hover)] px-1.5 text-[11px] leading-[18px] text-[var(--text-secondary)]">
              {count.toLocaleString()}
            </span>
          )}
          {dot && <i aria-hidden className="h-[6px] w-[6px] rounded-full bg-[var(--success)]" />}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Trades as an icon by the search: it's there to look at, not a step every user takes
 * like a backtest or going live, so it stays out of the main menu.
 */
export function TradesLink() {
  const api = useApi([]);
  // Filled orders on every exchange: the rows of the Trades page.
  const [filled, setFilled] = useState<number | null>(null);
  useEffect(() => {
    if (!api.ready) return;
    let cancelled = false;
    void api.commands
      .getOrderStats({ account_id: "", backtest_id: "" })
      .then((st) => {
        if (!cancelled) setFilled((st as OrderStats | null)?.health?.counts?.done ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.ready]);
  return (
    <NavLink
      to="/trades"
      title={filled != null ? `Trades: ${filled.toLocaleString()} filled orders` : "Trades"}
      aria-label="Trades"
      className={({ isActive }) =>
        cn(
          "flex h-11 w-11 items-center justify-center rounded-full no-underline transition-colors md:h-[26px] md:w-[26px]",
          isActive
            ? "text-[var(--primary)] md:bg-[var(--primary-soft)]"
            : "text-[var(--text-muted)] hover:text-[var(--text-primary)] md:hover:bg-[var(--bg-hover)]"
        )
      }
    >
      {/* A page with a clock: the history of orders filled on your exchanges. */}
      <FileClock size={16} strokeWidth={1.8} aria-hidden />
    </NavLink>
  );
}
